import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Paper, Button, Alert, Avatar } from "@mui/material";
import { Lock as LockIcon } from "@mui/icons-material";
import { OTPInput } from "components";
import { UserServices, NotistackServices } from "shared/services";
import { useAccount } from "shared/hooks";

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];

export const InactivityPinLock: React.FC = () => {
	const user = useAccount();
	const [locked, setLocked] = useState(false);
	const [pin, setPin] = useState<string[]>(Array(4).fill(""));
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const lastActivityRef = useRef<number>(Date.now());
	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	const pinEnabled = user?.pinEnabled ?? false;
	const timeoutMinutes = user?.pinTimeoutMinutes ?? 30;
	const timeoutMs = timeoutMinutes * 60 * 1000;

	const resetTimer = useCallback(() => {
		if (locked) return;
		lastActivityRef.current = Date.now();
	}, [locked]);

	// Listen for user activity
	useEffect(() => {
		if (!pinEnabled || locked) return;

		const handler = () => resetTimer();

		for (const event of ACTIVITY_EVENTS) {
			document.addEventListener(event, handler, { passive: true });
		}

		return () => {
			for (const event of ACTIVITY_EVENTS) {
				document.removeEventListener(event, handler);
			}
		};
	}, [pinEnabled, locked, resetTimer]);

	// Check inactivity periodically
	useEffect(() => {
		if (!pinEnabled || locked) {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			return;
		}

		intervalRef.current = setInterval(() => {
			const elapsed = Date.now() - lastActivityRef.current;
			if (elapsed >= timeoutMs) {
				setLocked(true);
				setPin(Array(4).fill(""));
				setError(null);
			}
		}, 10_000); // Check every 10 seconds

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [pinEnabled, locked, timeoutMs]);

	// Reset lock state when user changes (login/logout)
	useEffect(() => {
		if (!user) {
			setLocked(false);
		}
		lastActivityRef.current = Date.now();
	}, [user?.uid]);

	const handleUnlock = async (event?: React.FormEvent) => {
		event?.preventDefault();

		const pinCode = pin.join("");
		if (pinCode.length !== 4) {
			setError("Digite o PIN de 4 dígitos.");
			return;
		}

		setLoading(true);
		setError(null);

		try {
			await UserServices.verifyPin(pinCode);
			setLocked(false);
			lastActivityRef.current = Date.now();
			setPin(Array(4).fill(""));
		} catch (err: any) {
			setError("PIN incorreto. Tente novamente.");
			setPin(Array(4).fill(""));
		} finally {
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		await UserServices.signOut();
		setLocked(false);
	};

	if (!pinEnabled || !locked || !user) {
		return null;
	}

	return (
		<Box
			sx={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				zIndex: 99999,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				bgcolor: "rgba(0, 0, 0, 0.85)",
				backdropFilter: "blur(12px)",
			}}
		>
			<Paper
				elevation={8}
				sx={{
					p: 4,
					maxWidth: 420,
					width: "100%",
					mx: 2,
					borderRadius: 3,
					textAlign: "center",
				}}
			>
				<Avatar
					sx={{
						width: 64,
						height: 64,
						mx: "auto",
						mb: 2,
						bgcolor: "primary.main",
					}}
				>
					<LockIcon sx={{ fontSize: 32 }} />
				</Avatar>

				<Typography
					variant="h5"
					sx={{ fontWeight: 600, mb: 0.5 }}
				>
					Sessão Bloqueada
				</Typography>

				<Typography
					variant="body2"
					color="textSecondary"
					sx={{ mb: 1 }}
				>
					{user.displayName || user.email}
				</Typography>

				<Typography
					variant="body2"
					color="textSecondary"
					sx={{ mb: 3 }}
				>
					Digite seu PIN de 4 dígitos para desbloquear.
				</Typography>

				<form onSubmit={handleUnlock}>
					<Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
						<OTPInput
							value={pin}
							onChange={setPin}
							disabled={loading}
						/>
					</Box>

					{error && (
						<Alert
							severity="error"
							sx={{ mb: 2 }}
						>
							{error}
						</Alert>
					)}

					<Button
						fullWidth
						variant="contained"
						size="large"
						type="submit"
						disabled={loading || pin.join("").length !== 4}
						sx={{ mb: 1.5 }}
					>
						{loading ? "Verificando..." : "Desbloquear"}
					</Button>

					<Button
						fullWidth
						variant="text"
						size="small"
						onClick={handleLogout}
						disabled={loading}
						sx={{ color: "text.secondary", textTransform: "none" }}
					>
						Sair da conta
					</Button>
				</form>
			</Paper>
		</Box>
	);
};
